<!-- eslint-disable vue/no-static-inline-styles -->
<script setup lang="ts">
import { computed, ref } from 'vue';
import { $t } from '@/locales';
import { sendUserFeedback } from '@/service/api/data';

interface Emit {
    (event: 'update:visible', visible: boolean): void;
}
interface Props {
    visible: boolean;
    direction: any;
}

const emit = defineEmits<Emit>();
const props = defineProps<Props>();
const title = ref<string>('');
const body = ref<string>('');
const isVisible = ref<boolean>(true);
const isConfirmVisible = ref<boolean>(false);
const titleMaxLength = 100;
const bodyMaxLength = 2000;
const submitting = ref(false);

const BODY_MIN_LENGTH = 20;

const validateFeedback = () => {
    if (!body.value) {
        window.$message?.error($t('feedback.bodyCantBeEmpty'));
        return false;
    }
    if (body.value.length < BODY_MIN_LENGTH) {
        window.$message?.error($t('feedback.bodyTooShort'));
        return false;
    }
    return true;
};

const submitFeedback = async () => {
    console.log('Title:', title.value);
    console.log('Feedback:', body.value);

    if (!validateFeedback()) return;
    if (submitting.value) return;

    try {
        submitting.value = true;
        const response = await sendUserFeedback(title.value, body.value);
        if (response) {
            isVisible.value = false;
            isConfirmVisible.value = true;
            window.$message?.success($t('feedback.sendSuccess'));
        } else {
            throw new Error('Feedback submission failed');
        }
    } catch (error: any) {
        console.log('submitFeedback error: ', error);
        window.$message?.error($t('feedback.sendFailed'));
    } finally {
        submitting.value = false;
    }
};

const show = computed({
    get() {
        return props.visible;
    },
    set(visible: boolean) {
        emit('update:visible', visible);
    }
});
const closeConfirm = () => {
    title.value = '';
    body.value = '';
    show.value = false;
};
</script>

<template>
    <NModal v-model:show="show" auto-focus preset="card" transform-origin="center"
        style="width: 95%; max-width: 560px; border-radius: 8px" :style="{ direction: direction }"
        @after-enter="isConfirmVisible = false">
        <template #header>
            <p v-show="!isConfirmVisible" id="feedback-description" class="dialog-description">{{ $t('feedback.description') }}</p>
        </template>
        <div v-if="!isConfirmVisible" class="dialog-content">

            <form class="dialog-form" @submit.prevent="submitFeedback">
                <div class="form-group">
                    <label for="feedback-title-input" class="form-label">{{ $t('feedback.title') }}</label>
                    <input id="feedback-title-input" v-model="title" class="form-input"
                        :placeholder="$t('feedback.titleInputPlaceHolder')" :maxlength="titleMaxLength"
                        aria-describedby="feedback-title-description" aria-invalid="false" />
                </div>
                <div class="form-group">
                    <label for="feedback-body-input" class="form-label">{{ $t('feedback.body') }}</label>
                    <textarea id="feedback-body-input" v-model="body" class="form-textarea"
                        :placeholder="$t('feedback.bodyInputPlaceHolder')" :maxlength="bodyMaxLength"
                        aria-describedby="feedback-body-description" aria-invalid="false"></textarea>
                    <p id="feedback-body-description" class="form-description">{{ $t('feedback.footerDescription') }}
                    </p>
                </div>
                <div class="form-actions">
                    <button type="submit" class="btn-submit">{{ $t('feedback.sendButton') }}</button>
                </div>
            </form>
        </div>
        <div v-else id="confirm-dialog" role="dialog" aria-labelledby="confirm-title" tabindex="-1">
            <div class="dialog-content">
                <h2 id="confirm-title" class="dialog-title">{{ $t('feedback.thankYouTitle') }}</h2>
                <p class="dialog-description">{{ $t('feedback.thankYouMessage') }}</p>
                <div class="form-actions">
                    <button type="button" class="btn-submit" @click="closeConfirm">{{ $t('feedback.okayButton')
                        }}</button>
                </div>
            </div>
        </div>
    </NModal>
</template>

<style scoped>
.dialog-content {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    text-align: center;
}

.dialog-title {
    font-size: 1.25rem;
    font-weight: normal;
    margin: 0;
}

.dialog-description {
    font-size: 1rem;
    color: #666;
    overflow: auto;
    white-space: normal;
    word-wrap: break-word;
    padding: 0.5rem;
    text-align: center;
}

.dark .dialog-description {
    color: #9ca3af;
}

.dialog-form {
    display: flex;
    flex-direction: column;
    gap: 1rem;
}

.form-group {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

.form-label {
    text-align: start;
    font-size: 0.875rem;
    font-weight: medium;
}

.form-input,
.form-textarea {
    width: 100%;
    padding: 0.5rem;
    font-size: 0.875rem;
    border: 1px solid #ddd;
    border-radius: 0.5rem;
    background-color: #fff;
    color: #333;
    transition:
        border-color 0.3s,
        box-shadow 0.3s;
}

.dark .form-input,
.dark .form-textarea {
    color: #e5e7eb;
    border-color: #64748b;
    background-color: #374151;
}

.form-input:focus,
.form-textarea:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
}

.form-description {
    font-size: 0.875rem;
    color: #666;
}

.form-actions {
    display: flex;
    justify-content: center;
    gap: 1rem;
}

.btn-submit {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0.5rem 1.25rem;
    font-size: 0.875rem;
    font-weight: medium;
    color: #fff;
    background-color: #007bff;
    border: none;
    border-radius: 0.5rem;
    cursor: pointer;
    transition: background-color 0.3s;
}

.btn-submit:hover {
    background-color: #0056b3;
}

.btn-close {
    position: absolute;
    top: 1rem;
    right: 1rem;
    background: none;
    border: none;
    cursor: pointer;
    opacity: 0.7;
    transition: opacity 0.3s;
}

.btn-close:hover {
    opacity: 1;
}

.icon-close {
    width: 1.5rem;
    height: 1.5rem;
    stroke: #333;
}

.dark .icon-close {
    stroke: #e2e8f0;
}

.sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    border: 0;
}
</style>
